"use client";

import { ClipboardList, KeyRound, Settings2 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DataUsageTransparency() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const cards = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    icon: ClipboardList,
    title: "ASSESS YOUR NEEDS",
    body: "We evaluate your family's specific financial requirements and funeral cost projections. Our experts provide a clear, data-driven analysis of your coverage needs.",
    specLabel: "AVG. TIME",
    specValue: "20 MIN"
  }, {
    number: "02",
    icon: Settings2,
    title: "CUSTOMIZED QUOTE",
    body: "Every plan is tailored to your budget and goals. We provide a transparent, written breakdown of premiums and benefits before you make any commitment.",
    specLabel: "YOU APPROVE",
    specValue: "IN WRITING"
  }, {
    number: "03",
    icon: KeyRound,
    title: "SECURE YOUR LEGACY",
    body: "Once you choose your plan, we finalize the paperwork quickly. Rest easy knowing your family is protected from future financial burdens with a solid policy.",
    specLabel: "PROTECTION",
    specValue: "LIFETIME"
  }];
  const cautionBarStyle = {
    backgroundImage: "repeating-linear-gradient(135deg, var(--secondary) 0px, var(--secondary) 10px, var(--primary) 10px, var(--primary) 20px)"
  };
  return <section data-section-id="1983" id="datausagetransparency" className="relative overflow-hidden bg-background text-foreground py-24 md:py-32">
      <div className="container mx-auto px-6 md:px-10 lg:px-16">
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-2.5 h-2.5" style={{
              backgroundColor: "var(--secondary)"
            }} aria-hidden="true" />
              <span className="font-default text-xs md:text-sm uppercase" style={{
              letterSpacing: "0.18em",
              color: "var(--foreground)"
            }}>
                FL-002 / Our Planning Process
              </span>
            </div>

            <h2 className="font-default font-bold uppercase text-foreground" style={{
            fontSize: "clamp(2rem, 4.5vw, 4.25rem)",
            letterSpacing: "-0.015em",
            lineHeight: "0.95"
          }}>
              Simple Steps.{" "}
              <span className="relative inline-block">
                <span className={`absolute left-0 bottom-1 h-[0.55em] -z-0 transition-all duration-500 ease-out ${header.isVisible ? "w-full" : "w-0"}`} style={{
                backgroundColor: "var(--secondary)",
                transitionDelay: "350ms"
              }} aria-hidden="true" />
                <span className="relative z-10">Total Clarity.</span>
              </span>
            </h2>
          </div>

          <div className="lg:col-span-5 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-foreground/80 max-w-[48ch]">
              We believe in complete transparency. From your initial assessment to your final policy, we ensure you understand every detail of your coverage. No hidden clauses, just peace of mind for your family.
            </p>
          </div>
        </div>

        <div ref={cards.ref} className="relative mt-16 md:mt-20">
          <div className="absolute inset-0 hidden lg:flex items-center justify-between pointer-events-none select-none overflow-hidden" aria-hidden="true" style={{
          transform: "rotate(-2deg)"
        }}>
            {["01", "02", "03"].map((n, i) => <span key={n} data-index={i} className="font-default font-bold" style={{
            fontSize: "clamp(16rem, 22vw, 24rem)",
            color: "var(--foreground)",
            opacity: 0.06,
            lineHeight: 1
          }}>
                {n}
              </span>)}
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-0">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div data-index={i} key={step.number} className={`relative group flex transition-all duration-700 ease-out ${cards.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {i > 0 && <div className="hidden md:block w-3 self-stretch" style={cautionBarStyle} aria-hidden="true" />}

                  {i > 0 && <div className="md:hidden absolute -top-0 left-0 right-0 h-3 -translate-y-full" style={cautionBarStyle} aria-hidden="true" />}

                  <article className="relative flex-1 bg-card border p-8 md:p-10 overflow-hidden shadow-md rounded-lg" style={{
                borderColor: "var(--border)"
              }}>
                    <span className="absolute left-0 top-0 bottom-0 w-0 group-hover:w-1 transition-all duration-300 ease-out" style={{
                  backgroundColor: "var(--secondary)"
                }} aria-hidden="true" />

                    <div className="font-default font-bold leading-none mb-6 transition-colors duration-300" style={{
                  fontSize: "clamp(4rem, 7vw, 6rem)",
                  color: "var(--foreground)",
                  opacity: 0.85
                }}>
                      <span className="group-hover:[color:var(--secondary)] group-hover:opacity-100 transition-colors duration-300">
                        {step.number}
                      </span>
                    </div>

                    <div className="w-14 h-14 flex items-center justify-center border mb-6 rounded-lg" style={{
                  borderColor: "var(--border)"
                }}>
                      <Icon className="w-6 h-6" strokeWidth={1.75} style={{
                    color: "var(--foreground)"
                  }} />
                    </div>

                    <h3 className="font-default font-bold uppercase text-foreground mb-4" style={{
                  fontSize: "clamp(1.25rem, 1.6vw, 1.625rem)",
                  letterSpacing: "-0.01em",
                  lineHeight: "1.05"
                }}>
                      {step.title}
                    </h3>

                    <p className="text-foreground/75 leading-relaxed mb-8 font-default" style={{
                  fontSize: "0.9375rem",
                  lineHeight: "1.6"
                }}>
                      {step.body}
                    </p>

                    <div className="pt-5 border-t flex items-baseline justify-between gap-3" style={{
                  borderColor: "var(--border)"
                }}>
                      <span className="font-default uppercase text-foreground/60" style={{
                    fontSize: "0.6875rem",
                    letterSpacing: "0.12em"
                  }}>
                        {step.specLabel}
                      </span>
                      <span className="font-default uppercase font-medium text-foreground" style={{
                    fontSize: "0.75rem",
                    letterSpacing: "0.1em"
                  }}>
                        {step.specValue}
                      </span>
                    </div>
                  </article>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}